import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useNavigate, useParams } from 'react-router-dom';
import { USER_GROUP } from '../../models/types';
import { useApp } from '../../context/AppContext';
import { getApiUrl } from '../../utils/apiUrl';
import { SELECTED_APP_ID_KEY } from './SelectAppPage';

const MemberFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const isCreate = !id;

  const [loading, setLoading] = useState(isCreate ? false : true);
  const [loadError, setLoadError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    username: '',
    groups_list: [],
    is_active: true,
    is_staff: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);

  const canManageMembers = currentUser?.group === USER_GROUP.STAFF;

  const fetchAvailableGroups = useCallback(() => {
    const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
    if (!selectedAppId) return;
    const base = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!base) return;
    const token = localStorage.getItem('token');
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    axios
      .get(`${base}/${selectedAppId}/departments-staff/`, { headers })
      .then(({ data }) => {
        const depts = Array.isArray(data?.departments) ? data.departments : [];
        const byId = new Map();
        depts.forEach((d) => {
          (d?.staff ?? []).forEach((s) => {
            if (s != null && (s.id != null || s.name != null)) {
              byId.set(Number(s.id), { id: s.id, name: s.name ?? `#${s.id}` });
            }
          });
        });
        setAvailableGroups(Array.from(byId.values()));
      })
      .catch(() => setAvailableGroups([]));
  }, []);

  useEffect(() => {
    fetchAvailableGroups();
  }, [fetchAvailableGroups]);

  // Edit mode: fetch app-staff once and find user by id
  useEffect(() => {
    if (isCreate) return;
    const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
    if (!selectedAppId) {
      setLoadError('ไม่พบแอปที่เลือก');
      setLoading(false);
      return;
    }
    const base = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!base) {
      setLoadError('ไม่พบ URL API');
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    setLoading(true);
    setLoadError('');
    axios
      .get(`${base}/${selectedAppId}/app-staff/`, { headers })
      .then(({ data }) => {
        const departments = Array.isArray(data?.departments) ? data.departments : [];
        const topStaff = Array.isArray(data?.staff) ? data.staff : [];
        let staff = null;
        for (const dept of departments) {
          const list = Array.isArray(dept.staff) ? dept.staff : [];
          staff = list.find((s) => Number(s.id) === Number(id));
          if (staff) break;
        }
        if (!staff) staff = topStaff.find((s) => Number(s.id) === Number(id)) || null;
        if (!staff) {
          setLoadError('ไม่พบผู้ใช้');
          setLoading(false);
          return;
        }
        setEditingUser(staff);
        let groupsList = [];
        if (Array.isArray(staff.groups_list) && staff.groups_list.length > 0) {
          groupsList = staff.groups_list.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
        } else if (Array.isArray(staff.group_ids) && staff.group_ids.length > 0) {
          groupsList = staff.group_ids.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
        }
        setForm({
          username: staff.username ?? '',
          groups_list: groupsList,
          is_active: staff.is_active !== false,
          is_staff: staff.is_staff !== false,
        });
      })
      .catch((err) => {
        setLoadError(err?.response?.data?.detail || err?.message || 'โหลดข้อมูลไม่สำเร็จ');
      })
      .finally(() => setLoading(false));
  }, [id, isCreate]);

  // When editing and availableGroups loads, resolve staff.groups (names) to ids
  useEffect(() => {
    if (!editingUser || availableGroups.length === 0) return;
    if (Array.isArray(editingUser.groups_list) && editingUser.groups_list.length > 0) return;
    if (Array.isArray(editingUser.group_ids) && editingUser.group_ids.length > 0) return;
    if (!Array.isArray(editingUser.groups) || editingUser.groups.length === 0) return;
    const resolved = editingUser.groups
      .map((name) => availableGroups.find((g) => String(g.name || '').trim() === String(name || '').trim())?.id)
      .filter((gid) => gid != null)
      .map((x) => Number(x));
    setForm((prev) => ({ ...prev, groups_list: resolved }));
  }, [editingUser, availableGroups]);

  const handleSubmit = () => {
    const username = (form.username || '').trim();
    if (!username) {
      setSubmitError('กรุณากรอกชื่อผู้ใช้');
      return;
    }
    const base = getApiUrl('/api/user/users/').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    const headers = {
      accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {}),
    };
    setSubmitting(true);
    setSubmitError('');

    if (isCreate) {
      axios
        .post(
          `${base}/`,
          {
            username,
            groups_list: form.groups_list,
            is_active: form.is_active,
            is_staff: form.is_staff,
          },
          { headers }
        )
        .then(() => navigate('/manage-members', { state: { message: 'สร้างผู้ใช้สำเร็จ', severity: 'success' } }))
        .catch((err) => {
          setSubmitError(err?.response?.data?.detail || err?.response?.data?.username?.[0] || err?.message || 'สร้างผู้ใช้ไม่สำเร็จ');
        })
        .finally(() => setSubmitting(false));
      return;
    }

    // Edit
    const staff = editingUser;
    let initialGroupIds = [];
    if (Array.isArray(staff.groups_list) && staff.groups_list.length > 0) {
      initialGroupIds = staff.groups_list.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
    } else if (Array.isArray(staff.group_ids) && staff.group_ids.length > 0) {
      initialGroupIds = staff.group_ids.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
    } else if (Array.isArray(staff.groups) && staff.groups.length > 0 && availableGroups.length > 0) {
      initialGroupIds = staff.groups
        .map((name) => availableGroups.find((g) => String(g.name || '').trim() === String(name || '').trim())?.id)
        .filter((id) => id != null)
        .map((x) => Number(x));
    }
    const selectedIds = (form.groups_list || []).map((x) => Number(x)).filter((n) => !Number.isNaN(n));
    const add = selectedIds.filter((i) => !initialGroupIds.includes(i));
    const remove = initialGroupIds.filter((i) => !selectedIds.includes(i));
    const promises = [];
    if (username !== (staff.username ?? '')) {
      promises.push(axios.patch(`${base}/${editingUser.id}/`, { username }, { headers }));
    }
    if (add.length > 0 || remove.length > 0) {
      promises.push(axios.patch(`${base}/${editingUser.id}/groups/`, { add, remove }, { headers }));
    }
    if (promises.length === 0) {
      navigate('/manage-members', { state: { message: 'บันทึกสำเร็จ', severity: 'success' } });
      setSubmitting(false);
      return;
    }
    Promise.all(promises)
      .then(() => navigate('/manage-members', { state: { message: 'บันทึกสำเร็จ', severity: 'success' } }))
      .catch((err) => {
        setSubmitError(err?.response?.data?.detail || err?.response?.data?.username?.[0] || err?.message || 'บันทึกไม่สำเร็จ');
      })
      .finally(() => setSubmitting(false));
  };

  if (!canManageMembers) {
    return (
      <Box>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          ไม่มีสิทธิ์เข้าถึง หน้านี้ใช้ได้เฉพาะเจ้าหน้าที่ (Staff)
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
            กลับ
          </Button>
        </Box>
      </Box>
    );
  }

  if (!isCreate && (loading || loadError)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2, width: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.65)' }}>แก้ไขผู้ใช้</Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/manage-members')} sx={{ borderRadius: 2, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.45)', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)' } }}>
            กลับ
          </Button>
        </Box>
        {loadError ? (
          <Alert severity="error" sx={{ width: '100%', maxWidth: 720 }} onClose={() => setLoadError('')}>
            {loadError}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#845EC2' }} />
          </Box>
        )}
      </Box>
    );
  }

  const title = isCreate ? 'สร้างผู้ใช้งาน' : 'แก้ไขผู้ใช้';
  const submitLabel = isCreate ? 'สร้าง' : 'บันทึก';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, width: '100%' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manage-members')}
          sx={{ borderRadius: 2, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.45)', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)' } }}
        >
          กลับ
        </Button>
      </Box>
      <Card elevation={0} sx={{ width: '100%', maxWidth: 720, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.05)', bgcolor: 'rgba(255, 255, 255, 0.9)', overflow: 'hidden' }}>
        <Box
          sx={{
            background: 'linear-gradient(to right, #8E6CEB, #4092E9)',
            px: 2,
            pt: 1.5,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
         
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', flex: 1 }}>
            {title}
          </Typography>
        </Box>
        <CardContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="ชื่อผู้ใช้ (username)"
            value={form.username}
            onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
            sx={{ mb: 2 }}
          />
          {availableGroups.length > 0 ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>กลุ่ม (groups_list)</InputLabel>
              <Select
                multiple
                value={form.groups_list}
                onChange={(e) => setForm((prev) => ({ ...prev, groups_list: e.target.value }))}
                label="กลุ่ม (groups_list)"
                renderValue={(selected) =>
                  selected.map((gid) => availableGroups.find((g) => Number(g.id) === Number(gid))?.name ?? gid).join(', ')
                }
              >
                {availableGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name ?? g.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              label="กลุ่ม (group IDs คั่นด้วย comma)"
              value={form.groups_list.join(', ')}
              onChange={(e) => {
                const raw = e.target.value.replace(/\s/g, '');
                const ids = raw ? raw.split(',').map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n)) : [];
                setForm((prev) => ({ ...prev, groups_list: ids }));
              }}
              placeholder="เช่น 1, 2, 3"
              sx={{ mb: 2 }}
            />
          )}
          {isCreate && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    sx={{ color: '#845EC2', '&.Mui-checked': { color: '#845EC2' } }}
                  />
                }
                label="เปิดใช้งาน (is_active)"
                sx={{ display: 'block', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_staff}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_staff: e.target.checked }))}
                    sx={{ color: '#845EC2', '&.Mui-checked': { color: '#845EC2' } }}
                  />
                }
                label="เจ้าหน้าที่ (is_staff)"
                sx={{ display: 'block' }}
              />
            </>
          )}
        </CardContent>
        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/manage-members')}
            disabled={submitting}
            sx={{ borderRadius: 2, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.45)', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)' } }}
            startIcon={<ArrowBackIcon />}
          >
            กลับ
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ borderRadius: 2, bgcolor: '#845EC2', '&:hover': { bgcolor: '#6344A4' } }}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : submitLabel}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default MemberFormPage;
